import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Typography,
} from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as AdditionalInfoIcon } from "../../common/icons/additionalInfo.svg";
import { ReactComponent as DeleteIcon } from "../../common/icons/delete.svg";
import { ReactComponent as DuplicateIcon } from "../../common/icons/duplicate.svg";
import { ReactComponent as ManualOfferIcon } from "../../common/icons/manualOffer.svg";
import { ShiftOrderContext } from "./newOrder/short/OrderShortDataProvider";
import { getBundleRowValues } from "./newOrder/short/orderFormShortRowHandlers";
import { OrderFields } from "./orderTypes";

type Props = {
  open: boolean;
  anchorEl: any;
  onClose: () => void;
  values: OrderFields;
  rowId: string;
  id: string;
  bundleRowId: string | undefined;
  manuallyOffered: boolean;
  handleToggleManualOfferDialog: (id: string) => void;
  additionalInfoDisabled: boolean;
};

const OrderActionsMenu = ({
  open,
  anchorEl,
  onClose,
  values,
  rowId,
  id,
  bundleRowId,
  manuallyOffered,
  handleToggleManualOfferDialog,
  additionalInfoDisabled,
}: Props) => {
  const {
    handleDeleteRow,
    handleDuplicateRow,
    handleDuplicateBundledRow,
    handleToggleAdditionalInfo, // TODO: remove from context, use local state
    validationErrors,
    orderRows,
  } = useContext(ShiftOrderContext);

  const { t } = useTranslation(["common", "order"]);

  const manuallyOfferedLabel = manuallyOffered
    ? t("order:editTalent")
    : t("order:chooseTalent");

  // check if there are validation errors for date and time
  const rowSpecificDateTimeValidationErrors = validationErrors.find((error) => {
    if (error.field === "time" || error.field === "date") {
      if (!bundleRowId) {
        // single row, check if the error is for the current row
        return error.id === id;
      }
      // bundled row, check if the error is for any of the bundled rows
      const bundleValues = getBundleRowValues(orderRows, bundleRowId);
      return bundleValues?.some((bundle) => bundle.id === error.id);
    }
    // not a date or time error
    return false;
  });

  const manuallyOfferedDisabled =
    !values.workLocation.id ||
    !values.orderTemplate ||
    !values.date ||
    !values.time ||
    !!rowSpecificDateTimeValidationErrors;

  return (
    <Menu open={open} anchorEl={anchorEl} onClose={onClose}>
      <List dense disablePadding>
        <ListItem disablePadding>
          <ListItemButton
            disabled={manuallyOfferedDisabled}
            sx={{ paddingLeft: "13px", paddingRight: "13px" }}
            onClick={() => handleToggleManualOfferDialog(id)}
          >
            <ListItemIcon sx={{ minWidth: "13px", marginRight: "13px" }}>
              <ManualOfferIcon />
            </ListItemIcon>
            <ListItemText>
              <Typography>{manuallyOfferedLabel}</Typography>
            </ListItemText>
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            disabled={additionalInfoDisabled}
            sx={{ paddingLeft: "13px", paddingRight: "13px" }}
            onClick={() => {
              handleToggleAdditionalInfo(id, rowId);
              onClose();
            }}
          >
            <ListItemIcon sx={{ minWidth: "13px", marginRight: "13px" }}>
              <AdditionalInfoIcon />
            </ListItemIcon>
            <ListItemText>
              <Typography>{t("common:additionalInfo")}</Typography>
            </ListItemText>
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            sx={{ paddingLeft: "13px", paddingRight: "13px" }}
            onClick={() => {
              if (bundleRowId) {
                handleDuplicateBundledRow(rowId, id);
              } else {
                handleDuplicateRow(rowId);
              }

              onClose();
            }}
          >
            <ListItemIcon sx={{ minWidth: "13px", marginRight: "13px" }}>
              <DuplicateIcon />
            </ListItemIcon>
            <ListItemText>
              <Typography>{t("common:duplicate")}</Typography>
            </ListItemText>
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            sx={{ paddingLeft: "13px", paddingRight: "13px" }}
            onClick={() => handleDeleteRow(rowId, bundleRowId ? id : undefined)}
          >
            <ListItemIcon sx={{ minWidth: "13px", marginRight: "13px" }}>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText>
              <Typography sx={{ color: "#C50631" }}>
                {t("common:delete")}
              </Typography>
            </ListItemText>
          </ListItemButton>
        </ListItem>
      </List>
    </Menu>
  );
};

export default OrderActionsMenu;
